import React, { useEffect, useState } from 'react';
import { formatInTimezone } from '../../../utils/formatInTimezone';
import { getChangeTypeLabel, getFeatureIdDisplayName } from '../../../constants/analyticsConstants';
import { requestDisplayStatus } from '../../../utils/changeRequestStatus';
import { listZones } from '../../../services/zones';
import RuleLink from '../components/RuleLink';
import ZonePolygonPreview from './ZonePolygonPreview';

const ZONE_CHANGE_TYPES = new Set(['zone_create', 'zone_update', 'zone_delete']);

/**
 * Разбор заявки администратором (Part 4, разделы 7.3 и 7.4; ТЗ 28/29).
 *
 * ТЗ 28: главный визуальный элемент — кадр камеры с предложенным контуром,
 * а не JSON payload. Для zone_update/zone_delete заявка не всегда несёт
 * геометрию (можно менять только имя, а удаление вовсе не содержит
 * polygon) — тогда контур для показа берём из текущей зоны камеры
 * (listZones), а остальные зоны камеры рисуем на превью серым фоном для
 * контекста. Если зон загрузить не удалось — превью просто не покажет
 * фон из других зон, но не откажется показать предложенный контур, если он
 * есть в payload.
 *
 * Approve может вернуть 409 — заявка построена на конфигурации, которая уже
 * уехала вперёд, либо её успели разобрать. Повторять применение автоматически
 * запрещено: модалка показывает это состояние и оставляет решение человеку.
 *
 * Reject требует причину: отказ без объяснения клиенту бесполезен.
 *
 * `readOnly` (Task 13, «Мои заявки») — клиент открывает ту же карточку для
 * своей собственной заявки: превью и JSON нужны, а блок Approve/Reject —
 * административное действие, которого у клиента просто нет.
 */
const ChangeRequestDetailModal = ({
  request,
  busy = false,
  error = '',
  stale = false,
  readOnly = false,
  cameraLabel = '',
  onApprove,
  onReject,
  onClose,
}) => {
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState('');
  const [reasonError, setReasonError] = useState('');
  const [zones, setZones] = useState([]);

  const isZoneChange = ZONE_CHANGE_TYPES.has(request.change_type);

  // Заявка на зону без геометрии в payload (правка только имени, удаление) —
  // подтягиваем актуальный контур из зон камеры, чтобы всё равно было что
  // показать поверх кадра.
  useEffect(() => {
    if (!request.camera_id || !isZoneChange) { setZones([]); return undefined; }
    let cancelled = false;
    listZones(request.camera_id)
      .then((data) => { if (!cancelled) setZones(Array.isArray(data?.items) ? data.items : []); })
      .catch(() => { if (!cancelled) setZones([]); });
    return () => { cancelled = true; };
  }, [request.camera_id, request.zone_id, isZoneChange]);

  // FE-FIX06: аргумент — сама заявка, а не её статус: tombstone «Удалена»
  // считается из zone_deleted.
  const status = requestDisplayStatus(request);
  const impact = request.impact || {};
  const affectedRules = impact.affected_rules || [];
  const decided = ['approved', 'rejected', 'cancelled'].includes(request.status);

  const typeLabel = getChangeTypeLabel(request.change_type);
  const title = request.payload?.display_name || request.zone_display_name || typeLabel;
  const cameraDisplay = cameraLabel || request.camera_name || `Камера #${request.camera_id}`;
  const requestId = request.request_id || request.change_request_id || '';

  const matchedZone = zones.find((zone) => zone.zone_id === request.zone_id);
  const payloadPolygon = Array.isArray(request.payload?.polygon) ? request.payload.polygon : null;
  const previewPolygon = payloadPolygon || matchedZone?.polygon || null;
  const existingZones = zones.filter((zone) => zone.zone_id !== request.zone_id);

  const submitReject = () => {
    if (!reason.trim()) {
      setReasonError('Укажите причину отказа');
      return;
    }
    onReject(reason.trim());
  };

  const copyRequestId = () => {
    if (!requestId) return;
    navigator.clipboard?.writeText(requestId);
  };

  return (
    <>
      <div className="vio-modal-overlay" onClick={busy ? undefined : onClose} />
      <div className="vio-modal vio-modal--wide" role="dialog" aria-label="Заявка на изменение">
        <h3 className="vio-modal-title">{title}</h3>
        {title !== typeLabel && <p className="vio-cr-subtitle">{typeLabel}</p>}

        <div className="vio-cr-meta">
          <span className={`vio-badge vio-badge--${status.modifier}`}>{status.label}</span>
          <span>{cameraDisplay}</span>
          <span>{request.proposed_by?.display_name || 'Клиент'}</span>
          {request.created_at && <span>{formatInTimezone(request.created_at)}</span>}
          {request.reviewed_at && <span>Рассмотрена {formatInTimezone(request.reviewed_at)}</span>}
        </div>

        {request.camera_id && isZoneChange && Array.isArray(previewPolygon) && (
          <ZonePolygonPreview
            cameraId={request.camera_id}
            polygon={previewPolygon}
            existingZones={existingZones}
          />
        )}

        {stale && (
          <div className="vio-notice">
            Конфигурация камеры изменилась после подачи заявки — применить её нельзя.
            Клиенту нужно подать заявку заново.
          </div>
        )}

        {error && <p className="vio-field-error">{error}</p>}

        {!readOnly && rejecting && (
          <div className="vio-field">
            <label className="vio-field-label" htmlFor="vio-cr-reason">Причина отказа</label>
            <input
              id="vio-cr-reason"
              type="text"
              value={reason}
              disabled={busy}
              onChange={(event) => { setReason(event.target.value); setReasonError(''); }}
            />
            {reasonError && <p className="vio-field-error">{reasonError}</p>}
          </div>
        )}

        <div className="vio-modal-actions">
          <button type="button" className="vio-btn" disabled={busy} onClick={onClose}>
            Закрыть
          </button>

          {!readOnly && !decided && !rejecting && (
            <>
              <button
                type="button"
                className="vio-btn vio-btn--danger"
                disabled={busy}
                onClick={() => setRejecting(true)}
              >
                Отклонить
              </button>
              <button
                type="button"
                className="vio-btn vio-btn--primary"
                disabled={busy || stale}
                onClick={onApprove}
              >
                {busy ? 'Применяем…' : 'Одобрить'}
              </button>
            </>
          )}

          {!readOnly && rejecting && (
            <button
              type="button"
              className="vio-btn vio-btn--danger"
              disabled={busy}
              onClick={submitReject}
            >
              {busy ? 'Отклоняем…' : 'Подтвердить отказ'}
            </button>
          )}
        </div>

        <div className="vio-readonly-grid">
          <div>
            <div className="vio-readonly-label">Потребуется включить детекцию</div>
            <div className="vio-readonly-value">
              {(impact.features_to_enable || []).map(getFeatureIdDisplayName).join(', ') || '—'}
            </div>
          </div>
          <div>
            <div className="vio-readonly-label">Будет выключена детекция</div>
            <div className="vio-readonly-value">
              {(impact.features_to_disable || []).map(getFeatureIdDisplayName).join(', ') || '—'}
            </div>
          </div>
          <div>
            <div className="vio-readonly-label">Затронутые правила</div>
            <div className="vio-readonly-value">
              {affectedRules.length > 0
                ? affectedRules.map((rule, index) => (
                  <React.Fragment key={rule.rule_id || index}>
                    {index > 0 && ', '}
                    <RuleLink rule={rule} />
                  </React.Fragment>
                ))
                : '—'}
            </div>
          </div>
        </div>

        <details className="vio-cr-payload">
          <summary>Содержимое заявки</summary>
          <pre className="ved-snapshot-json">{JSON.stringify(request.payload ?? {}, null, 2)}</pre>
        </details>

        {requestId && (
          <div className="vio-cr-request-id">
            <span title={requestId}>Заявка №{requestId}</span>
            <button type="button" className="vio-btn vio-btn--link" onClick={copyRequestId}>
              Скопировать
            </button>
          </div>
        )}
      </div>
    </>
  );
};

export default ChangeRequestDetailModal;
